import { useState } from 'react';
import { Income, EditableIncome } from '../hooks/useIncomes';
import { PanelBase } from './PanelBase';
import { TableViewBase } from './TableViewBase';
import { amountForPeriod, Period } from '../utils';
import { showError, showSuccess } from 'src/toasts/toasts';
import { IncomeModal } from './IncomeModal';
import { useDisclosure } from '@mantine/hooks';
import { frequencyAmountCell } from '../components/cashflowCells';
import { truncatedTextOrNotRecorded } from '../notRecorded';
import { labelForIncomeType } from '../incomeTypes';
import { useCurrency } from 'src/common/hooks/useCurrency';

type IncomePanelProps = {
  items: Income[];
  onSave: (data: EditableIncome[]) => Promise<void>;
  onDelete: (ids: string[]) => Promise<void>;
  period: Period;
};

export function IncomePanel({ items = [], onSave, onDelete, period }: IncomePanelProps) {
  const currency = useCurrency();
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [selectedIncome, setSelectedIncome] = useState<EditableIncome | undefined>();

  // Reports the failure and lets it through: the modal keeps what was typed only if it hears
  // that the save failed, and the adviser would otherwise have to retype the whole row.
  const handleSave = async (income: EditableIncome) => {
    try {
      await onSave([income]);
    } catch (error) {
      showError('Failed to save income');
      throw error;
    }
    showSuccess('Income saved successfully');
    closeModal();
    setSelectedIncome(undefined);
  };

  const handleEdit = (income: Income) => {
    setSelectedIncome(income);
    openModal();
  };

  const handleAdd = () => {
    setSelectedIncome(undefined);
    openModal();
  };

  const handleDelete = async (income: Income) => {
    if (!income.id) return;
    try {
      await onDelete([income.id]);
      showSuccess('Income deleted');
    } catch {
      showError('Failed to delete income');
    }
  };

  const renderTableView = () => (
    <TableViewBase
      items={items}
      columns={[
        {
          key: 'incomeType',
          header: 'Type',
          sortable: true,
          renderValue: (_value, item) => labelForIncomeType(item.incomeType),
        },
        {
          key: 'description',
          header: 'Description',
          sortable: true,
          renderValue: (_value, item) => truncatedTextOrNotRecorded(item.description),
        },
        { key: 'frequency', header: 'Frequency', sortable: true },
        {
          key: 'amountPreTax',
          header: `Before tax (${period})`,
          sortable: true,
          sortValueGetter: item => amountForPeriod(item.amountPreTax, item.frequency, period),
          renderValue: (_value, item) =>
            frequencyAmountCell(period, item.amountPreTax, item.frequency, currency),
          align: 'right',
        },
        {
          key: 'amountPostTax',
          header: `After tax (${period})`,
          sortable: true,
          sortValueGetter: item => amountForPeriod(item.amountPostTax, item.frequency, period),
          renderValue: (_value, item) =>
            frequencyAmountCell(period, item.amountPostTax, item.frequency, currency),
          align: 'right',
        },
      ]}
      onEdit={handleEdit}
      onDelete={handleDelete}
    />
  );

  return (
    <>
      <PanelBase<EditableIncome>
        items={items}
        addButtonLabel="Add a new income"
        renderTableView={renderTableView}
        onAdd={handleAdd}
      />
      <IncomeModal
        opened={modalOpened}
        onClose={closeModal}
        onSubmit={handleSave}
        initialValues={selectedIncome}
      />
    </>
  );
}
